import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PlayIcon, PauseIcon, CheckIcon, ArrowRightIcon, ArrowRightOnRectangleIcon,
  TruckIcon, ClockIcon, QrCodeIcon, ExclamationTriangleIcon, XMarkIcon, FlagIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getWesSocket, disconnectWesSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';

// ============================================================
// Tipos
// ============================================================

type Station = { key: string; name: string; description?: string };
type WorkItem = {
  name: string;
  type: 'product' | 'service';
  quantity: number;
  completed?: boolean;
  completedBy?: string;
};
type Vehicle = {
  appointmentId: string;
  appointmentCode: string;
  status: string;
  checkedInAt?: string;
  serviceType?: string;
  client: { firstName?: string; lastName?: string; companyName?: string; phone?: string };
  vehicle: { licensePlate?: string; brand?: string; model?: string };
  stationQueue: string[];
  stationsCompleted: string[];
  currentStationKey?: string;
  serviceReportId: string | null;
  startedAt: string | null;
  items: WorkItem[];
  photos: any[];
};

// ============================================================
// Helpers
// ============================================================

const formatTime = (totalSec: number): string => {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const elapsedSeconds = (startedAt?: string | null, now: number = Date.now()): number => {
  if (!startedAt) return 0;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / 1000);
};

const fullName = (c: any) => c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'Cliente';

// ============================================================
// Reproducir sonido F1 al ingresar nuevo vehículo
// ============================================================

const playF1Sound = () => {
  try {
    const audio = new Audio('/sounds/f1-radio-notification.mp3');
    audio.volume = 0.6;
    audio.play().catch(() => { /* user interaction needed */ });
  } catch {}
};

// ============================================================
// Componente principal
// ============================================================

const MonitorTaller: React.FC = () => {
  const { stationKey = '' } = useParams<{ stationKey: string }>();
  const [searchParams] = useSearchParams();
  const monitorToken = searchParams.get('token') || '';
  const mode = searchParams.get('mode') === 'display' ? 'display' : 'operator';

  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [station, setStation] = useState<Station | null>(null);
  const [inProcess, setInProcess] = useState<Vehicle | null>(null);
  const [queue, setQueue] = useState<Vehicle[]>([]);
  const [tick, setTick] = useState(Date.now());

  // Operator session (solo modo operator)
  const [operatorSession, setOperatorSession] = useState<{ sessionToken: string; userName: string } | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // QR
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);


  // Tracking de vehículos vistos para detectar nuevos
  const seenVehicleIdsRef = useRef<Set<string>>(new Set());

  // Tick global para cronómetro
  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // 1) Auth de monitor
  useEffect(() => {
    if (!monitorToken || !stationKey) {
      setAuthError('URL inválida: faltan parámetros');
      setAuthLoading(false);
      return;
    }
    axios.post('/api/workshop/monitor/auth', { token: monitorToken, stationKey })
      .then((res) => {
        setStation(res.data.station);
        setAuthError(null);
      })
      .catch((err) => {
        setAuthError(err.response?.data?.error || 'Error de autenticación');
      })
      .finally(() => setAuthLoading(false));
  }, [monitorToken, stationKey]);

  // 2) Fetch state
  const fetchState = useCallback(async () => {
    if (!station || !monitorToken) return;
    try {
      const res = await axios.get('/api/workshop/monitor/state', {
        params: { monitorToken, stationKey: station.key }
      });
      const newQueue: Vehicle[] = res.data.queue || [];
      const newInProcess: Vehicle | null = res.data.inProcess || null;

      // Detectar vehículos nuevos en la cola
      const allIds = new Set<string>();
      [newInProcess, ...newQueue].forEach((v) => v && allIds.add(v.appointmentId));
      const seen = seenVehicleIdsRef.current;
      const newOnes = Array.from(allIds).filter((id) => !seen.has(id));
      if (seen.size > 0 && newOnes.length > 0) {
        // Hay vehículo nuevo después del primer fetch
        playF1Sound();
        const newest = [newInProcess, ...newQueue].find((v) => v && newOnes.includes(v.appointmentId));
        if (newest) {
          toast.success(
            `🚗 Nuevo vehículo: ${newest.vehicle?.licensePlate || ''} ${newest.vehicle?.brand || ''} ${newest.vehicle?.model || ''}`,
            { duration: 8000 }
          );
        }
      }
      seenVehicleIdsRef.current = allIds;

      setInProcess(newInProcess);
      setQueue(newQueue);
    } catch (err: any) {
      console.error('[Monitor] Error fetch state:', err);
    }
  }, [station, monitorToken]);

  useEffect(() => {
    if (!station) return;
    fetchState();
    const socket = getWesSocket();
    socket.on('reception:check-in', fetchState);
    socket.on('reception:advance', fetchState);
    socket.on('reception:undo', fetchState);
    socket.on('wes:update', fetchState);
    return () => {
      socket.off('reception:check-in', fetchState);
      socket.off('reception:advance', fetchState);
      socket.off('reception:undo', fetchState);
      socket.off('wes:update', fetchState);
      disconnectWesSocket();
    };
  }, [station, fetchState]);

  // QR para fotos del vehículo en proceso
  useEffect(() => {
    if (mode !== 'operator' || !inProcess || !monitorToken) {
      setQrUrl(null);
      return;
    }
    axios.get('/api/workshop/monitor/photo-token', {
      params: { monitorToken, appointmentId: inProcess.appointmentId }
    })
      .then((res) => {
        const url = `${window.location.origin}/upload-photos/${res.data.token}`;
        setQrUrl(url);
      })
      .catch(() => setQrUrl(null));
  }, [inProcess, monitorToken, mode]);

  // ===== Acciones (operator only) =====
  const requireOperator = (action: () => void) => {
    if (operatorSession) {
      action();
    } else {
      pendingActionRef.current = action;
      setShowPinModal(true);
    }
  };

  const handlePinSubmit = async () => {
    if (pinInput.length < 4) {
      toast.error('Ingresá tu PIN');
      return;
    }
    setPinLoading(true);
    try {
      const res = await axios.post('/api/workshop/monitor/operator-login', {
        pin: pinInput, monitorToken, stationKey
      });
      setOperatorSession({
        sessionToken: res.data.sessionToken,
        userName: res.data.operator.fullName
      });
      setPinInput('');
      setShowPinModal(false);
      toast.success(`Bienvenido ${res.data.operator.fullName}`);
      // Ejecutar acción pendiente
      const pending = pendingActionRef.current;
      pendingActionRef.current = null;
      if (pending) setTimeout(pending, 100);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'PIN incorrecto');
      setPinInput('');
    } finally {
      setPinLoading(false);
    }
  };

  const handleLogout = async () => {
    if (operatorSession) {
      try { await axios.post('/api/workshop/monitor/operator-logout', { sessionToken: operatorSession.sessionToken }); } catch {}
    }
    setOperatorSession(null);
    toast.info('Sesión de operario cerrada');
  };

  const callAction = async (endpoint: string, body: any = {}) => {
    if (!operatorSession) return;
    try {
      await axios.post(`/api/workshop/monitor/${endpoint}`, {
        sessionToken: operatorSession.sessionToken, ...body
      });
      fetchState();
    } catch (err: any) {
      if (err.response?.status === 401) {
        toast.error('Tu sesión expiró, ingresá el PIN nuevamente');
        setOperatorSession(null);
      } else {
        toast.error(err.response?.data?.error || 'Error en acción');
      }
    }
  };

  const handleStart = () => requireOperator(() => callAction('start', { appointmentId: inProcess?.appointmentId || queue[0]?.appointmentId }));
  const handlePause = () => requireOperator(() => callAction('pause', { appointmentId: inProcess?.appointmentId }));
  const handleAdvance = () => requireOperator(() => callAction('advance', { appointmentId: inProcess?.appointmentId }));
  const handleComplete = async () => {
    if (!(await confirmDialog('¿Completar este vehículo?'))) return;
    requireOperator(() => callAction('complete', { appointmentId: inProcess?.appointmentId }));
  };
  const handleToggleTask = (item: WorkItem, idx: number) => {
    if (!inProcess) return;
    // Necesitamos índice dentro del array (products o services) que tiene el SR
    const sameTypeItems = inProcess.items.filter((it) => it.type === item.type);
    const idxInType = sameTypeItems.indexOf(item);
    requireOperator(() => callAction('toggle-task', {
      appointmentId: inProcess.appointmentId,
      itemType: item.type,
      itemIndex: idxInType
    }));
  };

  // Atajos de teclado
  useEffect(() => {
    if (mode !== 'operator') return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (inProcess) handlePause(); else if (queue[0]) handleStart();
      } else if (e.key === 'Enter' && inProcess) {
        const firstUndone = inProcess.items.find((it) => !it.completed);
        if (firstUndone) handleToggleTask(firstUndone, 0);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [inProcess, queue, operatorSession, mode]);

  // ===== Render =====
  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-foreground">Cargando monitor...</div>;
  }
  if (authError || !station) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-8">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold mb-2">Monitor no autorizado</h1>
          <p className="text-muted-foreground">{authError}</p>
          <p className="text-sm text-muted-foreground mt-4">
            Pedile al admin la URL correcta del monitor de esta estación.
          </p>
        </div>
      </div>
    );
  }

  const elapsed = inProcess ? elapsedSeconds(inProcess.startedAt, tick) : 0;
  const totalTasks = inProcess?.items.length || 0;
  const completedTasks = inProcess?.items.filter((it) => it.completed).length || 0;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TruckIcon className="h-8 w-8 text-cyan-600 dark:text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold">{station.name}</h1>
            {station.description && <p className="text-xs text-muted-foreground">{station.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-mono font-semibold tabular-nums">
              {new Date(tick).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="text-xs text-muted-foreground">
              {new Date(tick).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          {mode === 'operator' && operatorSession && (
            <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
              <div className="text-sm">
                <div className="font-medium">{operatorSession.userName}</div>
                <div className="text-xs text-muted-foreground">Sesión activa</div>
              </div>
              <Tip label="Cerrar sesión">
              <Button size="icon" variant="ghost" onClick={handleLogout}>
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </Button>
              </Tip>
            </div>
          )}
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 p-6 overflow-hidden">
        {/* Vehículo actual */}
        <div className="bg-card border border-border rounded-xl p-6 flex flex-col">
          {inProcess ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xl text-cyan-600 dark:text-cyan-400">{inProcess.appointmentCode}</div>
                  <h2 className="text-4xl font-bold mt-1">{inProcess.vehicle?.licensePlate || '-'}</h2>
                  <p className="text-xl text-foreground/80 mt-1">
                    {inProcess.vehicle?.brand || ''} {inProcess.vehicle?.model || ''}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{fullName(inProcess.client)}</p>
                </div>
                {qrUrl && (
                  <Tip label="QR para subir fotos desde el celular">
                    <button
                      onClick={() => setShowQrModal(true)}
                      className="bg-foreground text-background rounded-lg p-2 flex flex-col items-center hover:scale-105 transition-transform"
                    >
                      <QrCodeIcon className="h-12 w-12" />
                      <span className="text-[10px] mt-1">Fotos</span>
                    </button>
                  </Tip>
                )}
              </div>

              {/* Cronómetro gigante */}
              <div className="bg-muted/50 border border-border rounded-lg p-4 my-4 text-center">
                <div className="text-xs uppercase text-muted-foreground mb-1">Tiempo en proceso</div>
                <div className="font-mono text-6xl tabular-nums font-bold text-emerald-600 dark:text-emerald-400">
                  {formatTime(elapsed)}
                </div>
                {totalTasks > 0 && (
                  <div className="text-sm text-muted-foreground mt-2">
                    {completedTasks} / {totalTasks} tareas completadas
                  </div>
                )}
              </div>

              {/* Checklist de tareas */}
              <div className="flex-1 overflow-y-auto">
                <h3 className="font-semibold text-muted-foreground mb-2 uppercase text-xs">Tareas</h3>
                {inProcess.items.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4">Sin tareas asignadas</p>
                ) : (
                  <div className="space-y-2">
                    {inProcess.items.map((item, idx) => (
                      <button
                        key={`${item.type}-${idx}`}
                        onClick={() => handleToggleTask(item, idx)}
                        disabled={mode !== 'operator'}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                          item.completed
                            ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200'
                            : 'bg-background border-border hover:bg-muted/50'
                        } ${mode === 'operator' ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        <div className={`flex items-center justify-center w-7 h-7 rounded border-2 ${
                          item.completed ? 'bg-emerald-500 border-emerald-500' : 'border-muted-foreground/40'
                        }`}>
                          {item.completed && <CheckIcon className="h-4 w-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <div className={`text-base ${item.completed ? 'line-through' : ''}`}>{item.name}</div>
                          {item.completedBy && (
                            <div className="text-xs opacity-70">por {item.completedBy}</div>
                          )}
                        </div>
                        <Badge variant="outline" className="text-[10px]">
                          x{item.quantity}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Acciones */}
              {mode === 'operator' && (() => {
                const hasNextStation = (inProcess.stationQueue || []).some(
                  (k) => k !== inProcess.currentStationKey && !(inProcess.stationsCompleted || []).includes(k)
                );
                return (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                    {inProcess.status === 'in_progress' ? (
                      <Button onClick={handlePause} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-base py-6">
                        <PauseIcon className="h-5 w-5 mr-2" /> Pausar
                      </Button>
                    ) : (
                      <Button onClick={handleStart} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-base py-6">
                        <PlayIcon className="h-5 w-5 mr-2" /> Iniciar
                      </Button>
                    )}
                    {hasNextStation ? (
                      <Button onClick={handleAdvance} variant="outline" className="flex-1 text-base py-6">
                        <ArrowRightIcon className="h-5 w-5 mr-2" />
                        Avanzar
                      </Button>
                    ) : (
                      <Button onClick={handleComplete} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-base py-6">
                        <CheckIcon className="h-5 w-5 mr-2" /> Completar
                      </Button>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              {queue.length > 0 && mode === 'operator' && (
                <Button onClick={handleStart} className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6">
                  <PlayIcon className="h-6 w-6 mr-2" /> Iniciar próximo
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Cola */}
        <div className="bg-card border border-border rounded-xl p-6 flex flex-col">
          <h3 className="font-semibold text-muted-foreground mb-4 uppercase text-xs flex items-center justify-between">
            <span>Cola ({queue.length})</span>
          </h3>
          <div className="flex-1 overflow-y-auto space-y-2">
            {queue.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">Sin vehículos en cola</p>
            ) : (
              queue.map((v, i) => (
                <div key={v.appointmentId} className="bg-background rounded-lg p-3 border border-border">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-mono text-sm text-cyan-600 dark:text-cyan-400">{v.appointmentCode}</div>
                      <div className="font-semibold mt-0.5">{v.vehicle?.licensePlate || '-'}</div>
                      <div className="text-xs text-muted-foreground">{v.vehicle?.brand} {v.vehicle?.model}</div>
                      <div className="text-xs text-muted-foreground mt-1">{fullName(v.client)}</div>
                    </div>
                    <Badge variant="outline">#{i + 1}</Badge>
                  </div>
                  {v.checkedInAt && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <ClockIcon className="h-3 w-3" />
                      <span>Espera: {formatTime(Math.floor((tick - new Date(v.checkedInAt).getTime()) / 1000))}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* PIN modal */}
      {mode === 'operator' && (
        <Dialog open={showPinModal} onOpenChange={(open) => { if (!open) { setShowPinModal(false); setPinInput(''); pendingActionRef.current = null; } }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Ingresá tu PIN</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter') handlePinSubmit(); }}
                autoFocus
                placeholder="••••"
                className="text-center text-3xl tracking-[0.5em] font-mono py-6"
              />
              <p className="text-xs text-muted-foreground text-center mt-2">
                Sesión activa por 30 minutos sin actividad
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowPinModal(false); setPinInput(''); }} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handlePinSubmit} disabled={pinLoading || pinInput.length < 4} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white">
                {pinLoading ? '...' : 'Ingresar'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}


      {/* QR modal */}
      {qrUrl && (
        <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Subir fotos desde el celular</DialogTitle>
            </DialogHeader>
            <div className="text-center py-4">
              <div className="bg-white p-3 rounded-lg inline-block">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUrl)}`}
                  alt="QR upload"
                  className="mx-auto"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-3 break-all">{qrUrl}</p>
              <p className="text-sm text-foreground mt-3">
                Escaneá el QR con la cámara del celular y subí las fotos del vehículo.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default MonitorTaller;
